import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { RefreshCw, Loader2, CheckCircle2, XCircle, Radio, Save } from 'lucide-react';

export default function CrmSyncTab() {
  const [cfg, setCfg] = useState(null);
  const [ping, setPing] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [key, setKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/crm-sync/status');
      setCfg(data.config);
      setPing(data.ping);
      setRecent(data.recent || []);
      setEnabled(!!data.config?.enabled);
      setBaseUrl(data.config?.base_url || '');
    } catch {
      toast.error('Could not load CRM sync status');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled, base_url: baseUrl };
      if (key.trim()) body.key = key.trim();
      await api.put('/super-admin/crm-sync/config', body);
      toast.success('CRM sync configuration saved');
      setKey('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const testConn = async () => {
    try {
      const { data } = await api.post('/super-admin/crm-sync/test', {});
      setPing(data.ping);
      data.ping?.ok ? toast.success('CRM reachable (ping OK)') : toast.error('CRM ping failed');
      await load();
    } catch { toast.error('Test failed'); }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post('/super-admin/crm-sync/backfill', {});
      toast.success(`Backfill: ${data.synced}/${data.total} synced, ${data.failed} failed`);
      await load();
    } catch { toast.error('Backfill failed'); }
    finally { setBackfilling(false); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 p-6" data-testid="crm-sync-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading CRM sync…</div>;
  }

  return (
    <div className="max-w-3xl" data-testid="crm-sync-tab">
      <div className="mb-6">
        <h2 className="text-xl font-black tracking-tighter flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          <Radio className="h-5 w-5 text-primary" /> CRM Sync
        </h2>
        <p className="text-sm text-slate-600 mt-2">
          Push registration, subscription, users and credit/payment events to the external Marketing CRM.
          Sending is best-effort and never blocks the app.
        </p>
      </div>

      {/* Health */}
      <div className="bg-white border border-slate-200 p-5 mb-5" data-testid="crm-sync-health">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            {ping?.ok
              ? <><CheckCircle2 className="h-4 w-4 text-green-600" /> <span className="text-green-700 font-medium">CRM reachable</span></>
              : <><XCircle className="h-4 w-4 text-red-500" /> <span className="text-red-600 font-medium">CRM unreachable</span></>}
            {ping?.status != null && <span className="text-xs text-slate-400 font-mono">HTTP {ping.status}</span>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={testConn} data-testid="crm-sync-test-btn">
              <Radio className="h-4 w-4 mr-2" /> Test
            </Button>
            <Button variant="outline" size="sm" className="rounded-none" onClick={runBackfill} disabled={backfilling} data-testid="crm-sync-backfill-btn">
              {backfilling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Backfill all orgs
            </Button>
          </div>
        </div>
      </div>

      {/* Config */}
      <div className="bg-white border border-slate-200 p-6 space-y-5 mb-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Enable CRM sync</Label>
            <p className="text-[11px] text-slate-500 mt-0.5">Turn outbound pushing on/off platform-wide.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="crm-sync-enabled-switch" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide font-medium">CRM base URL</Label>
          <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://…/api/marketing" className="rounded-none mt-1 font-mono text-sm"
            data-testid="crm-sync-baseurl" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide font-medium">Sync key (X-Sync-Key)</Label>
          <Input type="password" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder={cfg?.key_masked ? `Current: ${cfg.key_masked} — leave blank to keep` : 'Paste sync key'}
            className="rounded-none mt-1 font-mono text-sm" data-testid="crm-sync-key" />
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="rounded-none" data-testid="crm-sync-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save
          </Button>
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white border border-slate-200" data-testid="crm-sync-recent">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Recent sync activity</h3>
          <Button variant="ghost" size="sm" className="rounded-none" onClick={load} data-testid="crm-sync-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        {recent.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-400">No sync events yet.</div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-80 overflow-auto">
            {recent.map((r, i) => (
              <div key={i} className="px-5 py-2.5 flex items-center justify-between text-xs" data-testid={`crm-sync-log-${i}`}>
                <div className="flex items-center gap-2">
                  {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <XCircle className="h-3.5 w-3.5 text-red-500" />}
                  <span className="font-mono font-medium">/{r.endpoint}</span>
                  {r.event && <span className="text-slate-400">· {r.event}</span>}
                  {r.status != null && <span className="text-slate-400 font-mono">HTTP {r.status}</span>}
                </div>
                <span className="text-slate-400 font-mono">{r.ts?.slice(0, 19).replace('T', ' ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
